import React from 'react';
import { Layout, Row, Col, Segment, Button, Bubble } from 'tea-component';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import useData from './_hooks';
import { formatNum } from '@/pages/intelligence/utils/commonUtils';
import './index.scss';
import NetChart from './component/NetChart';
import SvgIcon from '@/pages/home/components/SvgIcon';
import MapChart from './component/MapChart';
import moment from 'moment';
import { dataSourceList } from './_config';
import EmptyPieChart from './component/EmptyPieChart';
import SingleLineChart from './component/SingleLineChart';
import { useIntl, getLocale, history } from 'umi';
import Loading from '@/pages/intelligence/components/Loading';
import NoData from '@/pages/intelligence/components/NoData';
import TipContext from '@/pages/home/components/TipContext';
import nameMap from '@/pages/dashboard/components/MapEchart/_config/nameMap.json';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
const { Body, Content } = Layout;

/** 每行 4 列；仅最右列用 top-end，避免居中气泡被视口防溢出推偏 */
const DATASOURCE_COL_COUNT = 4;
/** 最右列气泡右移（Popper skidding, px) */
const RIGHTMOST_BUBBLE_SKIDDING_PX = 70;
/** 箭头相对全量反补后再右移的微调（px），与 skidding 一并注入 SCSS */
const RIGHTMOST_BUBBLE_ARROW_NUDGE_PX = 2;
const RIGHTMOST_BUBBLE_STYLE: React.CSSProperties & {
  '--dbi-sm-datasource-bubble-skidding': string;
  '--dbi-sm-datasource-bubble-arrow-nudge': string;
} = {
  '--dbi-sm-datasource-bubble-skidding': `${RIGHTMOST_BUBBLE_SKIDDING_PX}px`,
  '--dbi-sm-datasource-bubble-arrow-nudge': `${RIGHTMOST_BUBBLE_ARROW_NUDGE_PX}px`,
};

export default function Overview(props) {
  const {
    summaryInfo,
    multipleAreaData,
    monthlyUpdatedData,
    marketCoverage,
    lastMonth,
    pieList,
    pieCompany,
    lineChartList,

    loadingDataSummary,
    loadingHistoricalMagnitude,
    loadingMonthlyUpdatedGames,
    loadingMarketsCoverage,
    loadingTopGamesCoverage,
    loadingCompanyCoverage,
    loadingMonthSummaryData,
  } = useData(props);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const imgInvert = ['Steam', 'Epic', 'PlayStation'];
  return (
    <Content className="dbi-source-metrics-overview">
      <Content.Header title={formatMessage({ id: 'dbi_overview' })} />
      <Content.Body full>
        <MarqueeBanner />
        <Row>
          <Col span={24}>
            <CardSlot title={formatMessage({ id: 'dbi-sm-data-summary' })} isShowRightDom="-none">
              <div>
                {loadingDataSummary ? (
                  <Loading />
                ) : (
                  <div className="dbi-sm-data-summary">
                    {summaryInfo ? (
                      Object.keys(summaryInfo).map((item, index) => (
                        <div className="dbi-sm-data-summary-item">
                          {/* <div>
                              <img
                                src={summaryInfo[item]['icon'] || ""}
                                style={{ width: '24px', height: '24px', verticalAlign: 'middle' }}
                              />
                            </div> */}
                          {/* 先放一个 后续 需要开发传数据 */}
                          <SvgIcon
                            className="dbi-sm-data-summary-icon"
                            type={summaryInfo[item].icon || ''}
                          />
                          <div className="dbi-sm-data-summary-text">
                            {locale === 'zh-CN'
                              ? summaryInfo[item].nameZh
                              : summaryInfo[item].nameEn}
                          </div>
                          <div className="dbi-sm-data-summary-num">
                            {formatNum(summaryInfo[item].num)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <NoData />
                    )}
                  </div>
                )}
              </div>
            </CardSlot>
          </Col>
        </Row>

        <Row className="dbi-sm-body-full">
          <Col span={12}>
            <CardSlot
              title={formatMessage({ id: 'dbi-sm-historical-magnitude' })}
              isShowRightDom="-none"
            >
              <div style={{ height: '310px' }}>
                {loadingHistoricalMagnitude ? (
                  <Loading />
                ) : multipleAreaData?.xAxis.length <= 0 ? (
                  <NoData />
                ) : (
                  <>
                    <div className="dbi-sm-historical-subtitle">
                      {multipleAreaData?.xAxis?.at(-1)}
                      <div className="dbi-sm-historical-subtitle-num">
                        {formatNum(multipleAreaData.totalNow)}
                        <div className="dbi-sm-text-change">
                          <div
                            className={
                              multipleAreaData.totalChange > 0
                                ? 'dbi-sm-text-green'
                                : 'dbi-sm-text-red'
                            }
                          >
                            {(multipleAreaData.totalChange * 100).toFixed(2)}%
                          </div>
                          <SvgIcon
                            type={
                              multipleAreaData.totalChange > 0 ? 'green-up-new' : 'red-down-new'
                            }
                            className="dbi-sm-text-svg"
                          />
                        </div>
                      </div>
                    </div>
                    <NetChart
                      xAxis={multipleAreaData.xAxis}
                      mobileData={multipleAreaData.mobileYAxis}
                      pcData={multipleAreaData.pcYAxis}
                      consoleData={multipleAreaData.consoleYAxis}
                    />
                  </>
                )}
              </div>
            </CardSlot>
          </Col>
          <Col span={12}>
            <CardSlot
              title={formatMessage({ id: 'dbi-sm-monthly-updated-games' })}
              isShowRightDom="-none"
            >
              <div style={{ height: '310px' }}>
                {loadingMonthlyUpdatedGames ? (
                  <Loading />
                ) : monthlyUpdatedData?.xAxis.length <= 0 ? (
                  <NoData />
                ) : (
                  <>
                    <div className="dbi-sm-historical-subtitle">
                      {monthlyUpdatedData?.xAxis?.at(-1)}
                      <div className="dbi-sm-historical-subtitle-num">
                        {formatNum(monthlyUpdatedData.totalNow)}
                        <div className="dbi-sm-text-change">
                          <div
                            className={
                              monthlyUpdatedData.totalChange > 0
                                ? 'dbi-sm-text-green'
                                : 'dbi-sm-text-red'
                            }
                          >
                            {(monthlyUpdatedData.totalChange * 100).toFixed(2)}%
                          </div>
                          <SvgIcon
                            type={
                              monthlyUpdatedData.totalChange > 0 ? 'green-up-new' : 'red-down-new'
                            }
                            className="dbi-sm-text-svg"
                          />
                        </div>
                      </div>
                    </div>
                    <NetChart
                      xAxis={monthlyUpdatedData.xAxis}
                      mobileData={monthlyUpdatedData.mobileYAxis}
                      pcData={monthlyUpdatedData.pcYAxis}
                      consoleData={monthlyUpdatedData.consoleYAxis}
                    />
                  </>
                )}
              </div>
            </CardSlot>
          </Col>
        </Row>

        <Row className="dbi-sm-body-full">
          <Col span={16}>
            <CardSlot
              title={
                <div className="dbi-sm-body-title">
                  {formatMessage({ id: 'dbi-sm-countries-coverage' })}
                  <span className="dbi-sm-body-text">
                    {`(${formatMessage({ id: 'dbi_sm_revenue_heat_map' })})`}
                  </span>
                </div>
              }
              isShowRightDom="-none"
            >
              <div style={{ height: '330px' }}>
                {loadingMarketsCoverage ? (
                  <Loading />
                ) : marketCoverage?.length <= 0 ? (
                  <NoData />
                ) : (
                  <>
                    <div className="dbi-sm-subtitle">{lastMonth}</div>
                    <MapChart
                      data={
                        getLocale() === 'zh-CN'
                          ? marketCoverage?.map((item: { name: string; value: number }) => ({
                              ...item,
                              name: nameMap[item.name],
                            }))
                          : marketCoverage
                      }
                    />
                  </>
                )}
              </div>
            </CardSlot>
          </Col>
          <Col span={8}>
            <CardSlot title={formatMessage({ id: 'dbi-sm-data-sources' })} isShowRightDom="-none">
              <div className="dbi-sm-icon-box">
                {dataSourceList?.map((item, index) => {
                  const isRightmost =
                    index % DATASOURCE_COL_COUNT === DATASOURCE_COL_COUNT - 1;
                  return (
                    <Bubble
                      key={item.nameEn}
                      dark
                      placement={isRightmost ? 'top-end' : 'top'}
                      arrowPointAtCenter={!isRightmost}
                      placementOffset={
                        isRightmost ? [RIGHTMOST_BUBBLE_SKIDDING_PX, 10] : 10
                      }
                      className={
                        isRightmost
                          ? 'dbi-sm-datasource-hover-bubble dbi-sm-datasource-hover-bubble--end'
                          : 'dbi-sm-datasource-hover-bubble'
                      }
                      style={isRightmost ? RIGHTMOST_BUBBLE_STYLE : undefined}
                      content={
                        <div style={{ whiteSpace: 'pre-line' }}>
                          {locale === 'zh-CN' ? item.hoverZh : item.hoverEn}
                        </div>
                      }
                    >
                      <div
                        className="dbi-sm-icon-item"
                        onClick={() => {
                          if (item?.sourceUrl) {
                            history.push(item.sourceUrl);
                          }
                        }}
                      >
                        <img
                          className={`${imgInvert.includes(item.nameEn) ? 'db-invert-unset' : ''}`}
                          src={item.icon}
                        />
                      </div>
                    </Bubble>
                  );
                })}
              </div>
            </CardSlot>
          </Col>
        </Row>

        <Row className="dbi-sm-body-full">
          <Col span={16}>
            <CardSlot
              title={
                <div className="dbi-sm-body-flex">
                  {formatMessage({ id: 'dbi-sm-top-games-coverage' })}
                  <TipContext tipText={formatMessage({ id: 'dbi-sm-games-coverage-tip' })} />
                </div>
              }
              isShowRightDom="-none"
            >
              <div style={{ height: '300px' }}>
                {loadingTopGamesCoverage ? (
                  <Loading />
                ) : pieList?.length <= 0 ? (
                  <NoData />
                ) : (
                  <>
                    <div className="dbi-sm-subtitle">{lastMonth}</div>
                    <div className="dbi-sm-pie-list">
                      {pieList?.map((item) => (
                        <div className="dbi-sm-pie-item">
                          <EmptyPieChart
                            year={lastMonth}
                            data={item.data}
                            // text={`TOP ${item.type.toUpperCase()}\nGAMES`}
                            text={formatMessage({ id: `dbi_sm_${item.type.toUpperCase()}` })}
                            color={item.color}
                          />
                          <div className="dbi-sm-pie-text">
                            <div className="dbi-sm-pie-text-change">
                              {formatNum(item.total_daily)}
                              <div
                                className={
                                  item.change > 0 ? 'dbi-sm-text-green' : 'dbi-sm-text-red'
                                }
                              >
                                {(item.change * 100).toFixed(2)}%
                              </div>
                              <SvgIcon
                                type={item.change > 0 ? 'green-up-new' : 'red-down-new'}
                                className="dbi-sm-text-svg"
                              />
                            </div>
                            <div className="dbi-sm-text-totel">
                              {formatMessage({ id: 'dbi_sm_all_games' })}: {formatNum(item.total)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </CardSlot>
          </Col>
          <Col span={8}>
            <CardSlot
              title={
                <div className="dbi-sm-body-flex">
                  {formatMessage({ id: 'dbi-sm-company-coverage' })}
                  <TipContext tipText={formatMessage({ id: 'dbi-sm-company-coverage-tip' })} />
                </div>
              }
              isShowRightDom="-none"
            >
              <div style={{ height: '300px' }}>
                {loadingCompanyCoverage ? (
                  <Loading />
                ) : JSON.stringify(pieCompany) === '{}' ? (
                  <NoData />
                ) : (
                  <>
                    <div className="dbi-sm-subtitle">{lastMonth}</div>
                    <div className="dbi-sm-pie-list">
                      {JSON.stringify(pieCompany) != '{}' && (
                        <div className="dbi-sm-pie-item">
                          <EmptyPieChart
                            year={lastMonth}
                            data={pieCompany.data}
                            // text={`TOP\nCOMPANY`}
                            text={formatMessage({ id: 'dbi_sm_top_company' })}
                            color={pieCompany.color}
                          />
                          <div className="dbi-sm-pie-text">
                            <div className="dbi-sm-pie-text-change">
                              {formatNum(pieCompany.total_daily)}
                              <div
                                className={
                                  pieCompany.change > 0 ? 'dbi-sm-text-green' : 'dbi-sm-text-red'
                                }
                              >
                                {(pieCompany.change * 100).toFixed(2)}%
                              </div>
                              <SvgIcon
                                type={pieCompany.change > 0 ? 'green-up-new' : 'red-down-new'}
                                className="dbi-sm-text-svg"
                              />
                            </div>
                            <div>
                              {formatMessage({ id: 'dbi_sm_all_games' })}:{' '}
                              {formatNum(pieCompany.total)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CardSlot>
          </Col>
        </Row>

        <Row className="dbi-sm-body-full">
          {lineChartList?.map((item) => (
            <Col span={8}>
              <CardSlot
                title={formatMessage({ id: `dbi-sm-${item.title}` })}
                isShowRightDom="-none"
              >
                <div className="dbi-sm-historical-subtitle">
                  {lastMonth}
                  <div className="dbi-sm-historical-subtitle-num">
                    {formatNum(item.totalNow)}
                    <div className="dbi-sm-text-change">
                      <div className={item.change > 0 ? 'dbi-sm-text-green' : 'dbi-sm-text-red'}>
                        {(item.change * 100).toFixed(2)}%
                      </div>
                      <SvgIcon
                        type={item.change > 0 ? 'green-up-new' : 'red-down-new'}
                        className="dbi-sm-text-svg"
                      />
                    </div>
                  </div>
                </div>
                <SingleLineChart
                  data={item.data}
                  color={item.color}
                  name={formatMessage({ id: `dbi-sm-${item.title}` })}
                />
              </CardSlot>
            </Col>
          ))}
        </Row>
      </Content.Body>
    </Content>
  );
}
